import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { getAdminMetrics } from '../../lib/api';
import { 
  Users, 
  Building, 
  Calendar,
  DollarSign,
  TrendingUp,
  ArrowRight,
  Loader2
} from 'lucide-react';

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        const response = await getAdminMetrics();
        setMetrics(response.data);
      } catch (error) {
        console.error('Failed to load metrics:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMetrics();
  }, []);

  const formatPrice = (cents) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Admin Dashboard</h1>
        <p className="text-slate-500 mt-1">Platform overview and management</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Users</p>
                <p className="text-3xl font-bold text-slate-900">{metrics?.total_users || 0}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {metrics?.users_by_role?.host || 0} hosts • {metrics?.users_by_role?.guest || 0} guests
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Listings</p>
                <p className="text-3xl font-bold text-slate-900">{metrics?.active_listings || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Building className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Bookings</p>
                <p className="text-3xl font-bold text-slate-900">{metrics?.total_bookings || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Gross Booking Value</p>
                <p className="text-3xl font-bold text-slate-900">{formatPrice(metrics?.gross_booking_value || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Revenue</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Gross Booking Value</p>
              <p className="text-2xl font-bold text-slate-900">{formatPrice(metrics?.gross_booking_value || 0)}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-slate-500">Platform Fees Earned</p>
              <p className="text-2xl font-bold text-green-700">{formatPrice(metrics?.platform_fees || 0)}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Bookings by Status</p>
              <div className="mt-2 space-y-1">
                {metrics?.bookings_by_status && Object.entries(metrics.bookings_by_status).map(([status, data]) => (
                  <div key={status} className="flex justify-between text-sm">
                    <span className="capitalize">{status}</span>
                    <span className="font-medium">{data.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/app/admin/users">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <Users className="w-8 h-8 text-primary" />
              <div>
                <h3 className="font-medium text-slate-900">Manage Users</h3>
                <p className="text-sm text-slate-500">View and suspend users</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/app/admin/properties">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <Building className="w-8 h-8 text-primary" />
              <div>
                <h3 className="font-medium text-slate-900">Manage Properties</h3>
                <p className="text-sm text-slate-500">Review and suspend listings</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/app/admin/bookings">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <Calendar className="w-8 h-8 text-primary" />
              <div>
                <h3 className="font-medium text-slate-900">Manage Bookings</h3>
                <p className="text-sm text-slate-500">View and issue refunds</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}
